import { useCallback } from 'react';
import { Navigate, useParams } from 'react-router';

import { useLanguage } from '../../contexts/language';
import { usePatientAttempt } from '../../hooks/patient-attempt';
import { useReviewChanges } from '../../hooks/review-changes';

import { BackLink } from '../back-link';
import { EarlierAttempts } from '../earlier-attempts';
import { LoadFailed } from '../load-failed';
import { PatientHeader } from '../patient-header';
import { PendingSignOut } from '../pending-sign-out';
import { ReviewAnswers } from '../review-answers';
import { ReviewPanel } from '../review-panel';
import { ReviewSummary } from '../review-summary';
import { SavePdf } from '../save-pdf';

import styles from './style.module.css';

// The review of one submitted test (C35), as drawn in the blueprint: the
// patient's header on the Tests tab, what the test is with its scores, every
// answer with a note per answer, and beside them the note on the whole test,
// Mark as reviewed and the earlier attempts. On a phone the clinician's
// panel comes before the answers; on a desktop it stays in view beside them.
// Answers and panel are keyed by the attempt, so moving to an earlier attempt
// never keeps an open answer from the one before; each note's text and save
// state belong to its own attempt in the note saves (#132). A sign-out under
// way or stopped short shows above the notes it concerns (#134).
export function ReviewAttempt() {
  const { text } = useLanguage();
  const { patientId, attemptId } = useParams();
  const { data, error, retry, reload } = usePatientAttempt({ patientId, attemptId });
  const { setReviewed } = useReviewChanges(attemptId);

  const reviewed = useCallback(function(next) {
    return setReviewed(next).then(function() {
      return reload();
    });
  }, [ setReviewed, reload ]);

  if (error != null) {
    return <LoadFailed onRetry={ retry }/>;
  }

  if (data === undefined) {
    return null;
  }

  const { patient, patientAttempt } = data;

  // A test reached under another of the clinician's patients is shown under
  // its own, so the header never names the wrong person.
  if (patientAttempt.patientId !== patient.id) {
    return <Navigate to={ `/patients/${ patientAttempt.patientId }/tests/${ patientAttempt.id }` } replace/>;
  }

  return (
    <section className={ styles.page }>
      <PatientHeader patient={ patient }/>

      <BackLink to={ `/patients/${ patient.id }/tests` }>{ text.allTests }</BackLink>

      <ReviewSummary attempt={ patientAttempt }/>

      <SavePdf href={ `/reports/attempts/${ patientAttempt.id }.pdf` }/>

      <PendingSignOut/>

      <div key={ patientAttempt.id } className={ styles.columns }>
        <ReviewAnswers
          attemptId={ patientAttempt.id }
          answers={ patientAttempt.answers }
          options={ patientAttempt.questionnaire.options }
          patientName={ patient.firstName }
        />

        <div className={ styles.side }>
          <ReviewPanel attempt={ patientAttempt } patientName={ patient.firstName } onReviewed={ reviewed }/>

          <EarlierAttempts patientId={ patient.id } earlier={ patientAttempt.earlier }/>
        </div>
      </div>
    </section>
  );
}
